/**
 * Is the setup guide switched on for this user? The app shell (useOnboardingGate) asks once per
 * browser session before it redirects to /welcome or shows the "finish setup" banner, so a user is
 * never pushed toward a guide that is switched off (the remote kill switch), not yet deployed, or
 * not available to their account.
 *
 * `updateOnboardingProfile({ action: 'peek' })` answers without writing anything. The answer is
 * cached per uid in sessionStorage (in memory when storage is blocked), so the call happens at
 * most once per session. Any failure counts as "not available" and is cached the same way: the
 * redirect and banner are nudges, and a nudge that fires without a working guide is worse than
 * none.
 */
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../lib/firebase';
import type { UpdateOnboardingProfileRequest, UpdateOnboardingProfileResponse } from '../types';

export const AVAILABILITY_KEY = 'rc.onboarding.available';

const PEEK_TIMEOUT_MS = 15_000;

interface CachedAvailability {
  uid: string;
  enabled: boolean;
  /** When the answer was received (ms since epoch). */
  at: number;
}

let memoryCache: CachedAvailability | null = null;
const inFlight = new Map<string, Promise<boolean>>();

function isCached(value: unknown): value is CachedAvailability {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<CachedAvailability>;
  return (
    typeof candidate.uid === 'string' &&
    typeof candidate.enabled === 'boolean' &&
    typeof candidate.at === 'number' &&
    Number.isFinite(candidate.at)
  );
}

function readStored(): CachedAvailability | null {
  try {
    const raw = window.sessionStorage.getItem(AVAILABILITY_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return isCached(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function store(uid: string, enabled: boolean): void {
  const entry: CachedAvailability = { uid, enabled, at: Date.now() };
  memoryCache = entry;
  try {
    window.sessionStorage.setItem(AVAILABILITY_KEY, JSON.stringify(entry));
  } catch {
    // Blocked storage: the in-memory copy still holds for this page load.
  }
}

/** The cached answer for this user, or null when it has not been asked yet this session. */
export function getCachedAvailability(uid: string): boolean | null {
  if (memoryCache?.uid === uid) return memoryCache.enabled;
  const stored = readStored();
  return stored?.uid === uid ? stored.enabled : null;
}

/** Forgets every cached answer (tests, and any future explicit refresh). */
export function clearOnboardingAvailabilityCache(): void {
  memoryCache = null;
  inFlight.clear();
  try {
    window.sessionStorage.removeItem(AVAILABILITY_KEY);
  } catch {
    // Nothing stored that could be cleared.
  }
}

/** Resolves true only after a successful peek that reports the guide as enabled. Never rejects. */
export function peekOnboardingAvailability(uid: string): Promise<boolean> {
  const cached = getCachedAvailability(uid);
  if (cached !== null) return Promise.resolve(cached);
  const pending = inFlight.get(uid);
  if (pending) return pending;

  const peek = httpsCallable<UpdateOnboardingProfileRequest, UpdateOnboardingProfileResponse>(
    functions,
    'updateOnboardingProfile',
    { timeout: PEEK_TIMEOUT_MS },
  );
  const request = peek({ action: 'peek' })
    .then((result) => result.data?.ok === true && result.data.enabled === true)
    .catch((caught: unknown) => {
      console.warn('[onboarding] availability check failed', (caught as { code?: string } | null)?.code);
      return false;
    })
    .then((enabled) => {
      store(uid, enabled);
      return enabled;
    })
    .finally(() => {
      inFlight.delete(uid);
    });
  inFlight.set(uid, request);
  return request;
}
